import { useState, useRef, useCallback } from 'react';
import { motion } from 'motion/react';
import { Link } from '@tanstack/react-router';
import CaseStudyCard, { type CaseStudyId } from '@/components/CaseStudyCard';
import eclipseLogoAsset from '@/assets/eclipse-paving-logo-400.webp.asset.json';
import eclipseLogoLightAsset from '@/assets/eclipse-paving-logo-light-400.webp.asset.json';
import bowlScoreBoardLogoAsset from '@/assets/bowlscoreboard-logo.svg.asset.json';
import bowlScoreBoardLogoLightAsset from '@/assets/bowlscoreboard-logo-light.svg.asset.json';
import { useTheme } from '@/lib/theme-context';

const fgMuted = 'hsl(var(--muted-foreground))';

function EclipseLogo() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  return (
    <img
      src={isLight ? eclipseLogoLightAsset.url : eclipseLogoAsset.url}
      alt="Eclipse Paving"
      width={400}
      height={116}
      decoding="async"
      className="h-10 w-auto object-contain"
      draggable={false}
    />
  );
}


// NOTE: text-only "logos" (White Home Solutions) were removed —
// we only show real image logo assets here. To add another client logo, create
// a small logo component like BowlScoreBoardLogo below and add it to BASE_LOGOS.



function BowlScoreBoardLogo() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  return (
    <img
      src={isLight ? bowlScoreBoardLogoLightAsset.url : bowlScoreBoardLogoAsset.url}
      alt="BowlScoreBoard"
      width={750}
      height={165}
      decoding="async"
      className="h-10 w-auto object-contain"
      draggable={false}
    />
  );
}

type LogoDef = {
  id: string;
  Component: React.FC;
  href: string | null;
  caseStudy?: CaseStudyId;
  onHover?: (v: boolean, el?: HTMLElement, caseStudy?: CaseStudyId) => void;
};

const BASE_LOGOS: Omit<LogoDef, 'onHover'>[] = [
  { id: 'eclipse', Component: EclipseLogo, href: '/case-studies', caseStudy: 'eclipse' },
  { id: 'bowlscoreboard', Component: BowlScoreBoardLogo, href: '/case-studies', caseStudy: 'bowlscoreboard' },
];

function LogoItem({ id, Component, href, caseStudy, onHover }: LogoDef) {
  const ref = useRef<HTMLAnchorElement | HTMLDivElement>(null);
  const className = 'flex items-center justify-center px-6 opacity-70';
  const style: React.CSSProperties = { minWidth: '120px' };

  const handleEnter = useCallback(() => {
    if (onHover) {
      const el = ref.current;
      onHover(true, (el as HTMLElement) ?? undefined, caseStudy);
    }
  }, [onHover, caseStudy]);

  const handleLeave = useCallback(() => {
    if (onHover) onHover(false, undefined, caseStudy);
  }, [onHover, caseStudy]);

  const hoverProps = onHover
    ? { onMouseEnter: handleEnter, onMouseLeave: handleLeave }
    : {};

  if (href) {
    if (/^https?:\/\//.test(href)) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          style={style}
          aria-label={`${id} website`}
          {...hoverProps}
        >
          <Component />
        </a>
      );
    }
    return (
      <Link ref={ref as React.Ref<HTMLAnchorElement>} to={href} className={className} style={style} aria-label={`${id} case study`} {...hoverProps}>
        <Component />
      </Link>
    );
  }


  return (
    <div ref={ref as React.Ref<HTMLDivElement>} className={className} style={style}>
      <Component />
    </div>
  );
}

export default function TrustLogos() {
  const [showCard, setShowCard] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const [activeCase, setActiveCase] = useState<CaseStudyId>('eclipse');
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHover = useCallback((v: boolean, el?: HTMLElement, caseStudy?: CaseStudyId) => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (v) {
      if (caseStudy) setActiveCase(caseStudy);
      setShowCard(true);
      if (el) {
        const rect = el.getBoundingClientRect();
        setPopupPos({
          top: rect.top - 12,
          left: rect.left + rect.width / 2,
        });
      }
    } else {
      leaveTimerRef.current = setTimeout(() => {
        setShowCard(false);
        setPopupPos(null);
      }, 80);
    }
  }, []);

  const logos: LogoDef[] = BASE_LOGOS.map((logo) => ({ ...logo, onHover: handleHover }));

  const renderItems = (keyPrefix: string) =>
    logos.map((logo) => <LogoItem key={`${keyPrefix}-${logo.id}`} {...logo} />);

  return (
    <motion.div
      className="relative w-full mt-8 lg:mt-10"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.55 }}
    >
      <p
        className="text-center font-medium uppercase tracking-[0.2em] mb-4"
        style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontSize: 'clamp(0.55rem, 0.9vw, 0.7rem)',
          color: fgMuted,
        }}
      >
        Trusted by businesses we&apos;re proud to support
      </p>

      <div className="relative">
        {/* Hover popup — case study snippet for the hovered logo */}
        {popupPos && (
          <div
            onMouseEnter={() => setShowCard(true)}
            onMouseLeave={() => {
              setShowCard(false);
              setPopupPos(null);
            }}
            className="fixed z-50 w-[320px] transition-all duration-200"
            style={{
              top: popupPos.top,
              left: popupPos.left,
              opacity: showCard ? 1 : 0,
              transform: `translateX(-50%) translateY(-100%)`,
              pointerEvents: showCard ? 'auto' : 'none',
            }}
          >
            <CaseStudyCard caseStudy={activeCase} />
          </div>
        )}

        <div
          className="overflow-hidden"
          style={{
            transform: 'scale(0.95)',
            transformOrigin: 'top center',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)',
            maskImage:
              'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)',
          }}
        >
          <div
            className="flex w-max animate-marquee"
            style={{ animationPlayState: showCard ? 'paused' : 'running' }}
          >
            {renderItems('a')}
            {renderItems('b')}
            {renderItems('c')}
            {renderItems('d')}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
